// SplashScreen — v2 (Feb 2026).
//
// User-directed redesign:
//   • Background: WHITE with warm orangish-yellow decorative elements
//     (floating gears, arcs, dots) so the screen breathes and feels
//     premium instead of dull-navy.
//   • Logo: transparent `swami-icon-new.png` (no white box around it).
//   • Text: rendered with a Space-Cadet-to-Golden gradient fill using
//     `background-clip: text` for a proper brand-locked look.
//   • Sequence: logo rolls in → "SWAMI SOFTWARE" fades in beside it with
//     letter-spacing shift → golden underline sweeps → "PRODUCTION
//     TRACKER" pops with a bouncy scale-in → whole splash exits.
//
// Runs once per browser session (guarded by sessionStorage.splashSeen).

import { useEffect, useState } from "react";

const LOGO_URL = "/swami-icon-new.png";
const DURATION = 5000;  // 5s per user request — keep the premium moment breathing.

export default function SplashScreen({ onDone, onComplete }) {
  const done = onDone || onComplete;
  const [phase, setPhase] = useState("mount");
  useEffect(() => {
    const t1 = setTimeout(() => setPhase("active"), 80);
    const t2 = setTimeout(() => setPhase("exit"), DURATION - 500);
    const t3 = setTimeout(() => { setPhase("done"); done && done(); }, DURATION);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [done]);
  if (phase === "done") return null;

  return (
    <>
      <style>{`
        @keyframes swLogoRoll {
          0%   { transform: translateX(-160px) rotate(-540deg) scale(0.2); opacity: 0; }
          60%  { transform: translateX(0)     rotate(0deg)     scale(1.18); opacity: 1; }
          82%  { transform: translateX(0)     rotate(0deg)     scale(0.94); }
          100% { transform: translateX(0)     rotate(0deg)     scale(1);   opacity: 1; }
        }
        @keyframes swLogoDrift {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-6px); }
        }
        @keyframes swWordFade {
          from { opacity: 0; transform: translateY(10px); letter-spacing: 0.02em; }
          to   { opacity: 1; transform: translateY(0);    letter-spacing: 0.14em; }
        }
        @keyframes swTaglinePop {
          0%   { opacity: 0; transform: scale(0.5); }
          65%  { opacity: 1; transform: scale(1.16); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes swUnderline {
          from { width: 0;    opacity: 0; }
          to   { width: 100%; opacity: 1; }
        }
        @keyframes swBgOrbit {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(360deg); }
        }
        @keyframes swBgOrbitRev {
          from { transform: rotate(0deg);   }
          to   { transform: rotate(-360deg); }
        }
        @keyframes swFadeOut {
          from { opacity: 1; transform: scale(1); }
          to   { opacity: 0; transform: scale(1.04); }
        }

        /* Gradient text — brand blue → gold. background-clip:text needs
           these two lines for cross-browser rendering. */
        .sw-gradient-text {
          background: linear-gradient(115deg, #1D2951 0%, #2A4A9A 30%, #D4A843 70%, #F0B84B 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
        .sw-gradient-text-gold {
          background: linear-gradient(90deg, #D4A843 0%, #F0B84B 50%, #B45309 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }
      `}</style>

      <div
        aria-hidden="true"
        className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
        style={{
          background: "radial-gradient(ellipse at 30% 20%, #FFF9E8 0%, #FFFFFF 42%, #FFFFFF 100%)",
          animation: phase === "exit" ? "swFadeOut 500ms ease-in forwards" : undefined,
        }}
        data-testid="splash-screen"
      >
        {/* ── Decorative background — floating orange arcs/dots/gears ── */}
        <BgOrnaments />

        <div className="relative flex flex-col items-center gap-8 z-10">
          {/* Logo — deliberately oversized so it dominates the composition */}
          <div
            style={{
              animation: phase === "active"
                ? "swLogoRoll 1400ms cubic-bezier(0.22,1,0.36,1) forwards, swLogoDrift 3.6s ease-in-out 1600ms infinite"
                : "swLogoRoll 0ms forwards",
              opacity: 0,
            }}
          >
            <img src={LOGO_URL} alt="Swami"
              className="w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 object-contain drop-shadow-[0_36px_60px_rgba(212,168,67,0.45)]" />
          </div>

          {/* Wordmark — smaller than the logo but still readable */}
          <div className="flex flex-col items-center gap-3">
            <div
              style={{
                opacity: phase === "active" ? 1 : 0,
                animation: phase === "active" ? "swWordFade 1100ms ease-out 1100ms forwards" : undefined,
              }}
            >
              <div className="font-black tracking-[0.18em] text-xl md:text-3xl sw-gradient-text uppercase text-center"
                style={{ fontFamily: 'Chivo, sans-serif' }}>
                Swami Software
              </div>
              <div className="h-[3px] mt-2 rounded-full mx-auto"
                style={{
                  background: "linear-gradient(90deg, transparent, #D4A843, #F0B84B, #D4A843, transparent)",
                  animation: phase === "active" ? "swUnderline 1000ms ease-out 2000ms forwards" : undefined,
                  width: 0,
                }} />
            </div>

            {/* Product tag — subordinate to both logo and wordmark */}
            <div
              className="text-[11px] md:text-[13px] tracking-[0.48em] font-black sw-gradient-text-gold uppercase select-none text-center"
              style={{
                opacity: 0,
                animation: phase === "active" ? "swTaglinePop 800ms cubic-bezier(0.34,1.56,0.64,1) 2600ms forwards" : undefined,
              }}
            >
              Production Tracker
            </div>
          </div>
        </div>

        {/* Footer — corporate slug (kept minimal and refined) */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-[0.5em] uppercase font-bold text-[#B45309]/70"
          style={{ opacity: phase === "active" ? 1 : 0, transition: "opacity 800ms 3200ms" }}>
          Manufacturing Intelligence · Since 2004
        </div>
      </div>
    </>
  );
}

/* ── Floating background ornaments — soft orange gears + arcs.
      Kept purely decorative and pointer-events:none. ── */
function BgOrnaments() {
  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Left large orbiting gear */}
      <svg className="absolute -left-24 top-1/3 opacity-[0.10] text-[#D4A843]"
        style={{ animation: "swBgOrbit 22s linear infinite" }}
        width="380" height="380" viewBox="0 0 100 100" fill="currentColor">
        <GearPath />
      </svg>
      {/* Right small counter-rotating gear */}
      <svg className="absolute -right-16 top-16 opacity-[0.14] text-[#F0B84B]"
        style={{ animation: "swBgOrbitRev 14s linear infinite" }}
        width="220" height="220" viewBox="0 0 100 100" fill="currentColor">
        <GearPath />
      </svg>
      {/* Bottom mid gear */}
      <svg className="absolute left-1/2 -translate-x-1/2 -bottom-16 opacity-[0.08] text-[#D4A843]"
        style={{ animation: "swBgOrbit 30s linear infinite" }}
        width="300" height="300" viewBox="0 0 100 100" fill="currentColor">
        <GearPath />
      </svg>

      {/* Soft golden dots scattered */}
      {[
        [8, 12, 6], [92, 22, 4], [15, 78, 5], [82, 82, 7], [50, 8, 4], [45, 92, 5],
      ].map(([x, y, r], i) => (
        <span key={i}
          className="absolute rounded-full bg-[#F0B84B]/40"
          style={{ left: `${x}%`, top: `${y}%`, width: r * 2, height: r * 2 }} />
      ))}

      {/* Two curved arcs */}
      <svg className="absolute top-8 left-0 opacity-[0.15]" width="500" height="180" viewBox="0 0 500 180" fill="none">
        <path d="M0 140 Q 250 -40 500 140" stroke="#D4A843" strokeWidth="2" strokeDasharray="6 8" />
      </svg>
      <svg className="absolute bottom-8 right-0 opacity-[0.15]" width="500" height="180" viewBox="0 0 500 180" fill="none">
        <path d="M0 40 Q 250 220 500 40" stroke="#F0B84B" strokeWidth="2" strokeDasharray="6 8" />
      </svg>
    </div>
  );
}

/* Simple 12-tooth gear SVG path used for background ornaments. */
function GearPath() {
  return (
    <path d="M50 15 L54 5 L58 15 L68 12 L67 22 L76 28 L70 36 L79 42 L72 48 L79 55 L70 62 L76 68 L67 76 L68 84 L58 82 L54 92 L50 82 L46 92 L42 82 L32 84 L33 76 L24 68 L30 62 L21 55 L28 48 L21 42 L30 36 L24 28 L33 22 L32 12 L42 15 L46 5 Z M50 35 A15 15 0 1 0 50 65 A15 15 0 1 0 50 35 Z" />
  );
}
